class MonadicError extends Error{constructor(m,p,e,a,c){super(m);this.name='MonadicError';this.path=p;this.expected=e;this.actual=a;this.callStack=c}};
function typeError(p,e,v){const a=v===null?'null':typeof v;const err=new MonadicError('Expected '+e+" for '"+p+"', got "+a,p,e,a);const c=globalThis.__tjs?.getConfig?.();if(c?.logTypeErrors)console.error('[TJS TypeError] '+err.message);if(c?.throwTypeErrors)throw err;return err};
function isMonadicError(v){return v instanceof Error&&v.name==='MonadicError'&&'path' in v};
const __stack=[];function pushStack(n){__stack.push(n)}function popStack(){__stack.pop()}function getStack(){return[...__stack]};
function Type(d,p,e){const t={description:d,__runtimeType:true};if(typeof p==='function'){t.check=p;t.default=e??null}else{const ex=e??p;t.default=ex;t.check=v=>{if(ex===null)return true;return typeof v===typeof ex}}return t};
const __tjs = globalThis.__tjs?.createRuntime?.() ?? {typeError,isMonadicError,pushStack,popStack,getStack,Type};
/* tjs <- share.ts */

/*#
# share

`share()` synchronizes state across browser tabs and windows. Pass it
boxed proxies from `tosi()` and those paths will be kept in sync via
`BroadcastChannel` and persisted to `IndexedDB`.

```
import { tosi, share } from 'tosijs'

const { app } = tosi({
  app: { user: null, settings: { theme: 'light' } }
})

const { restored } = await share(app.user, app.settings)

if (restored.includes(app.user)) {
  // another tab was already running — we inherited its state
}
```

The first tab to call `share()` seeds the store. Subsequent tabs
inherit that data, overwriting their `tosi()` defaults. After setup,
changes in any tab propagate to all others in real-time.

## What to share

Share **small, session-level state**: user identity, preferences,
auth tokens, UI mode, active selections, cache-invalidation keys.

Don't share large datasets directly. Instead, share query metadata
(URLs, cache keys, timestamps) and let each tab fetch or cache the
data independently. This keeps the sync layer fast and avoids
hitting `BroadcastChannel` or `IndexedDB` with multi-megabyte writes.

## How it works

- Changes are sent as **deltas** via `BroadcastChannel` — only the
  changed path and its value are transmitted, not the entire root
  object.
- **Persistence** uses `IndexedDB` (async, no size limit). Full
  root-path snapshots are written on a debounced schedule for
  cold-start recovery.

## API

    share(...proxiesOrPaths: (BoxedProxy | string)[]): Promise<{ restored: (BoxedProxy | string)[] }>

- Accepts boxed proxies (from `tosi()`) or string paths
- Returns `{ restored }` — the subset of arguments whose values were
  overwritten from pre-existing stored data
- Idempotent: sharing the same path twice is a no-op
- Throws if an argument is neither a proxy nor a string

To clear shared state (e.g. on logout), set the values to their
empty/default state. The change will propagate to all tabs and
persist.

## Live Demo

Drag the squares around, then click **New Window** to open a second
copy. Drag in either window and watch the other update in real-time.

```html
<div class="draggable" data-key="red"></div>
<div class="draggable" data-key="green"></div>
<div class="draggable" data-key="blue"></div>
<button class="spawn">New Window</button>
```
```css
.preview {
  touch-action: none;
  min-height: 200px;
}

.draggable {
  position: absolute;
  width: 50px;
  height: 50px;
  cursor: move;
  border-radius: 6px;
}

.draggable[data-key="red"]   { background: #f008; }
.draggable[data-key="green"] { background: #0f08; }
.draggable[data-key="blue"]  { background: #00f8; }

.spawn {
  position: absolute;
  bottom: 8px;
  right: 8px;
}
```
```js
import { tosi, share, xin } from 'tosijs'
import { trackDrag } from 'tosijs-ui'

const { squares } = tosi({
  squares: {
    red:   { x: 20,  y: 20 },
    green: { x: 120, y: 20 },
    blue:  { x: 220, y: 20 },
  }
})

await share(squares)

const draggables = [...preview.querySelectorAll('.draggable')]

function render() {
  for (const el of draggables) {
    const key = el.dataset.key
    el.style.left = xin.squares[key].x + 'px'
    el.style.top = xin.squares[key].y + 'px'
  }
}

render()
squares.observe(render)

function dragItem(event) {
  const el = event.target.closest('.draggable')
  if (!el) return
  const key = el.dataset.key
  const start = { ...xin.squares[key] }
  trackDrag(event, (dx, dy, event) => {
    xin.squares[key] = { x: start.x + dx, y: start.y + dy }
    render()
    return event.type === 'mouseup'
  })
}

preview.addEventListener('mousedown', dragItem)
preview.addEventListener('touchstart', dragItem, { passive: true })

preview.querySelector('.spawn').addEventListener('click', () => {
  window.open(location.href)
})
```
*/

import { registry } from './registry'

import { getByPath, setByPath } from './by-path'

import { touch, observe, updates } from './path-listener'

import { tosiPath } from './metadata'

import { debounce } from './throttle'

const CHANNEL_NAME = 'tosijs-share';

const DB_NAME = 'tosijs-share';

const STORE_NAME = 'shared';

const DB_VERSION = 1;

const ShareMessage = Type('ShareMessage', undefined, { type: 'tosijs-share', path: '', value: null, origin: '' })

const sharedPaths = new Set();

const inboundPaths = new Set();

const debouncedSaves = new Map();

let channel = null;

let origin = '';

let db = null;

export const ShareStore = Type('ShareStore')

let store = null;

/* line 181 */
export function setShareStore(s) {
  try {
  __tjs.pushStack('share.ts:181:setShareStore');
  if (s instanceof Error) return s;
  if ((typeof s !== 'object' || s === null || Array.isArray(s))) return __tjs.typeError('share.ts:181:setShareStore.s', 'object', s);

    store = s;

  } finally { __tjs.popStack(); }
}
setShareStore.__tjs = {
  "params": {
    "s": {
      "type": {
        "kind": "object",
        "shape": {}
      },
      "required": true,
      "default": null
    }
  },
  "source": "share.ts:181"
}

/* line 187 */
/* TODO: TS types degraded — return: Promise<IDBDatabase> */
function openDB() {
    if (db != null)
        return Promise.resolve(db);
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            req.result.createObjectStore(STORE_NAME);
        };
        req.onsuccess = () => {
            db = req.result;
            resolve(db);
        };
        req.onerror = () => reject(req.error);
    });
}
openDB.__tjs = {
  "params": {},
  "source": "share.ts:187"
}

const idbStore = {
    async get(key) {
        const database = await openDB();
        return new Promise((resolve, reject) => {
            const tx = database.transaction(STORE_NAME, 'readonly');
            const req = tx.objectStore(STORE_NAME).get(key);
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
    },
    async set(key, value) {
        const database = await openDB();
        return new Promise((resolve, reject) => {
            const tx = database.transaction(STORE_NAME, 'readwrite');
            tx.objectStore(STORE_NAME).put(value, key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    },
};

/* line 223 */
function getStore() {
    return store ?? idbStore;
}
getStore.__tjs = {
  "params": {},
  "returns": {
    "type": {
      "kind": "object",
      "shape": {}
    }
  },
  "unsafeReturn": true,
  "source": "share.ts:223"
}

/* line 229 */
/* TODO: TS types degraded — return: data is ShareMessage */
function isShareMessage(data) {
  try {
  __tjs.pushStack('share.ts:231:isShareMessage');
  if (data instanceof Error) return data;

    return (data != null &&
        data.type === 'tosijs-share' &&
        typeof data.path === 'string');

  } finally { __tjs.popStack(); }
}
isShareMessage.__tjs = {
  "params": {
    "data": {
      "type": {
        "kind": "any"
      },
      "required": false
    }
  },
  "source": "share.ts:231"
}

/* line 237 */
function findSharedRoot(changedPath) {
  try {
  __tjs.pushStack('share.ts:237:findSharedRoot');
  if (changedPath instanceof Error) return changedPath;
  if (typeof changedPath !== 'string') return __tjs.typeError('share.ts:237:findSharedRoot.changedPath', 'string', changedPath);

    for (const shared of sharedPaths) {
        if (changedPath === shared || changedPath.startsWith(shared + '.')) {
            return shared;
        }
    }
    return undefined;

  } finally { __tjs.popStack(); }
}
findSharedRoot.__tjs = {
  "params": {
    "changedPath": {
      "type": {
        "kind": "string"
      },
      "required": true,
      "default": null
    }
  },
  "returns": {
    "type": {
      "kind": "union",
      "members": [
        {
          "kind": "string"
        },
        {
          "kind": "undefined"
        }
      ]
    }
  },
  "unsafeReturn": true,
  "source": "share.ts:237"
}

/* line 246 */
function isInbound(changedPath) {
  try {
  __tjs.pushStack('share.ts:246:isInbound');
  if (changedPath instanceof Error) return changedPath;
  if (typeof changedPath !== 'string') return __tjs.typeError('share.ts:246:isInbound.changedPath', 'string', changedPath);

    for (const path of inboundPaths) {
        if (changedPath === path || changedPath.startsWith(path + '.')) {
            return true;
        }
    }
    return false;

  } finally { __tjs.popStack(); }
}
isInbound.__tjs = {
  "params": {
    "changedPath": {
      "type": {
        "kind": "string"
      },
      "required": true,
      "default": null
    }
  },
  "returns": {
    "type": {
      "kind": "boolean"
    }
  },
  "unsafeReturn": true,
  "source": "share.ts:246"
}

/* line 255 */
function applyInbound(path, value) {
  try {
  __tjs.pushStack('share.ts:255:applyInbound');
  if (path instanceof Error) return path;
  if (value instanceof Error) return value;
  if (typeof path !== 'string') return __tjs.typeError('share.ts:255:applyInbound.path', 'string', path);

    inboundPaths.add(path);
    setByPath(registry, path, value);
    touch(path);
    updates().then(() => {
        inboundPaths.delete(path);
    });

  } finally { __tjs.popStack(); }
}
applyInbound.__tjs = {
  "params": {
    "path": {
      "type": {
        "kind": "string"
      },
      "required": true,
      "default": null
    },
    "value": {
      "type": {
        "kind": "any"
      },
      "required": false
    }
  },
  "source": "share.ts:255"
}

/* line 264 */
function initChannel() {
    if (channel != null)
        return channel;
    origin = crypto.randomUUID();
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event) => {
        const msg = event.data;
        if (!isShareMessage(msg))
            return;
        if (msg.origin === origin)
            return;
        // Check that the message path falls under a shared root
        if (findSharedRoot(msg.path) === undefined)
            return;
        applyInbound(msg.path, msg.value);
    };
    return channel;
}
initChannel.__tjs = {
  "params": {},
  "returns": {
    "type": {
      "kind": "object",
      "shape": {}
    }
  },
  "unsafeReturn": true,
  "source": "share.ts:264"
}

/* line 283 */
function broadcast(path, value) {
  try {
  __tjs.pushStack('share.ts:283:broadcast');
  if (path instanceof Error) return path;
  if (value instanceof Error) return value;
  if (typeof path !== 'string') return __tjs.typeError('share.ts:283:broadcast.path', 'string', path);

    if (channel == null)
        return;
    const msg = {
        type: 'tosijs-share',
        path,
        value,
        origin,
    };
    channel.postMessage(msg);

  } finally { __tjs.popStack(); }
}
broadcast.__tjs = {
  "params": {
    "path": {
      "type": {
        "kind": "string"
      },
      "required": true,
      "default": null
    },
    "value": {
      "type": {
        "kind": "any"
      },
      "required": false
    }
  },
  "source": "share.ts:283"
}

/* line 294 */
function schedulePersist(rootPath) {
  try {
  __tjs.pushStack('share.ts:294:schedulePersist');
  if (rootPath instanceof Error) return rootPath;
  if (typeof rootPath !== 'string') return __tjs.typeError('share.ts:294:schedulePersist.rootPath', 'string', rootPath);

    if (!debouncedSaves.has(rootPath)) {
        debouncedSaves.set(rootPath, debounce(() => {
            const current = getByPath(registry, rootPath);
            getStore().set(rootPath, current);
        }, 500));
    }
    debouncedSaves.get(rootPath)();

  } finally { __tjs.popStack(); }
}
schedulePersist.__tjs = {
  "params": {
    "rootPath": {
      "type": {
        "kind": "string"
      },
      "required": true,
      "default": null
    }
  },
  "source": "share.ts:294"
}

/* line 307 */
export async function share(...proxies) {
  try {
  __tjs.pushStack('share.ts:312:share');
  if (proxies instanceof Error) return proxies;
  if (proxies !== undefined && !Array.isArray(proxies)) return __tjs.typeError('share.ts:312:share.proxies', 'array', proxies);

    // Graceful degradation for SSR/Node
    if (typeof BroadcastChannel === 'undefined') {
        return { restored: [] };
    }
    initChannel();
    const restored = [];
    const s = getStore();
    for (const proxy of proxies) {
        const path = typeof proxy === 'string' ? proxy : tosiPath(proxy);
        if (path === undefined) {
            throw new Error('share() requires boxed proxies or string paths. Got a non-proxy value.');
        }
        // Idempotent — skip if already shared
        if (sharedPaths.has(path))
            continue;
        sharedPaths.add(path);
        // Restore from store or seed it
        const stored = await s.get(path);
        if (stored !== undefined) {
            setByPath(registry, path, stored);
            touch(path);
            restored.push(proxy);
        }
        else {
            const value = getByPath(registry, path);
            await s.set(path, value);
        }
        // Outbound observer: sync local changes to other tabs
        observe((changedPath) => changedPath === path || changedPath.startsWith(path + '.'), (changedPath) => {
            if (isInbound(changedPath))
                return;
            const rootPath = findSharedRoot(changedPath);
            if (rootPath === undefined)
                return;
            // Broadcast the delta at the changed path
            const value = getByPath(registry, changedPath);
            broadcast(changedPath, value);
            // Debounced full root-path persist for cold start
            schedulePersist(rootPath);
        });
    }
    return { restored };

  } finally { __tjs.popStack(); }
}
share.__tjs = {
  "params": {
    "proxies": {
      "type": {
        "kind": "array",
        "items": {
          "kind": "null"
        }
      },
      "required": false
    }
  },
  "returns": {
    "type": {
      "kind": "object",
      "shape": {
        "restored": {
          "kind": "array",
          "items": {
            "kind": "null"
          }
        }
      }
    }
  },
  "unsafeReturn": true,
  "source": "share.ts:312"
}